import React, { useState, useMemo, useCallback } from 'react'
import { Order, desc, stableSort } from '@/utils/sorting/index'

/**
 * @hook useTableSort
 * @param {C[]} dataSource - The data to be sorted
 * @param {I} initialState - A prop from the data to initialize the state
 * @return List of rows sorted by a specific field and condition. (order, orderBy, sortedRows, createSortHandler)
 */
const useTableSort = <C, I extends keyof C>(
  dataSource: C[],
  initialState: I,
  initialOrder?: Order,
) => {
  const [order, setOrder] = useState<Order>(initialOrder || 'asc')
  const [orderBy, setOrderBy] = useState<keyof C>(initialState)

  const sortRequestHandler = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      property: keyof C,
    ) => {
      const isDesc = orderBy === property && order === 'desc'
      setOrder(isDesc ? 'asc' : 'desc')
      setOrderBy(property)
    },
    [order, orderBy],
  )

  const getSortType = useCallback(
    <K extends keyof C>(
      sortOrder: Order,
      sortOrderBy: K,
    ): ((a: C, b: C) => number) => {
      return sortOrder === 'desc'
        ? (a, b) => desc(a, b, sortOrderBy)
        : (a, b) => -desc(a, b, sortOrderBy)
    },
    [order, orderBy], // eslint-disable-line
  )

  const createSortHandler = useCallback(
    (property: keyof C) => {
      return (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        sortRequestHandler(event, property)
      }
    },
    [order, orderBy, dataSource], // eslint-disable-line
  )

  const sortedRows = useMemo(
    () => stableSort(dataSource, getSortType(order, orderBy)),
    [dataSource, order, orderBy, getSortType],
  )

  return {
    order,
    orderBy,
    sortedRows,
    createSortHandler,
  }
}

export default useTableSort
